import * as React from 'react';
import { ArrowRoundForwardIcon, Greyscale } from 'glints-aries';
import { Icon } from 'glints-aries/lib/@next';
import { IconNames } from 'glints-aries/lib/@next/Icon/icons/icons';
import {
  defineMessages,
  FormattedMessage,
  type MessageDescriptor,
} from 'react-intl';

import { useEmployersURL } from 'src/common/hooks';
import { AssetType } from 'src/components/GlintsPicture/interfaces';

import { getTalentHubLandingLinks } from '../constants';
import * as Styled from './OneGlints.sc';

type SolutionKey = 'tapjobs' | 'talenthub';

interface Solution {
  key: SolutionKey;
  tabIcon: IconNames;
  tabLabel: MessageDescriptor;
  title: MessageDescriptor;
  lede: MessageDescriptor;
  features: MessageDescriptor[];
  cta: MessageDescriptor;
  rating: MessageDescriptor;
  metaIcon?: IconNames;
  /**
   * Optional banner image rendered in the visual slot for this tab.
   * When omitted, the default mock candidate card is shown instead.
   * Path is relative to the SG landing OSS asset root, e.g.
   * "og/talenthub.png".
   */
  bannerImage?: string;
  bannerAlt?: string;
}

const messages = defineMessages({
  tapjobsTab: {
    id: 'landing-sg.solutions.tabs.tapjobs',
    defaultMessage: 'Glints TapJobs',
  },
  tapjobsTitle: {
    id: 'landing-global.solutions.tapjobs.title',
    defaultMessage:
      'Post jobs for free, and reach professionals across Southeast Asia',
  },
  tapjobsLede: {
    id: 'landing-global.solutions.tapjobs.lede',
    defaultMessage:
      'Built for teams hiring at speed. Publish in minutes and start reviewing applicants the same day.',
  },
  tapjobsFeature1: {
    id: 'landing-sg.solutions.tapjobs.feature.1',
    defaultMessage: 'Unlimited job postings & applicants',
  },
  tapjobsFeature2: {
    id: 'landing-sg.solutions.tapjobs.feature.2',
    defaultMessage: 'AI-powered candidate matching',
  },
  tapjobsFeature3: {
    id: 'landing-sg.solutions.tapjobs.feature.3',
    defaultMessage: 'Built-in ATS and messaging',
  },
  tapjobsCta: {
    id: 'landing-sg.solutions.tapjobs.cta',
    defaultMessage: 'Post a job',
  },
  tapjobsRating: {
    id: 'landing-sg.solutions.tapjobs.rating',
    defaultMessage: '{stars} {score} from {count} reviews',
  },
  talenthubTab: {
    id: 'landing-sg.solutions.tabs.talenthub',
    defaultMessage: 'Glints TalentHub',
  },
  talenthubTitle: {
    id: 'landing-global.solutions.talenthub.title',
    defaultMessage: 'Build and manage fully remote teams anywhere, end-to-end.',
  },
  talenthubLede: {
    id: 'landing-global.solutions.talenthub.lede',
    defaultMessage:
      'We handle hiring, payroll, and compliance in 150+ countries so you can focus on building your team.',
  },
  talenthubFeature1: {
    id: 'landing-sg.solutions.talenthub.feature.1',
    defaultMessage: 'Pre-vetted candidate shortlists',
  },
  talenthubFeature2: {
    id: 'landing-global.solutions.talenthub.feature.2',
    defaultMessage: 'Employer-of-record in 150+ countries',
  },
  talenthubFeature3: {
    id: 'landing-sg.solutions.talenthub.feature.3',
    defaultMessage: 'Payroll, tax & compliance handled',
  },
  talenthubCta: {
    id: 'landing-sg.solutions.talenthub.cta',
    defaultMessage: 'Book a consult',
  },
  talenthubRating: {
    id: 'landing-sg.solutions.talenthub.rating',
    defaultMessage: 'Average 2-week time-to-shortlist',
  },
});

const solutions: Record<SolutionKey, Solution> = {
  tapjobs: {
    key: 'tapjobs',
    tabIcon: 'ri-search-line',
    tabLabel: messages.tapjobsTab,
    title: messages.tapjobsTitle,
    lede: messages.tapjobsLede,
    features: [
      messages.tapjobsFeature1,
      messages.tapjobsFeature2,
      messages.tapjobsFeature3,
    ],
    cta: messages.tapjobsCta,
    rating: messages.tapjobsRating,
  },
  talenthub: {
    key: 'talenthub',
    tabIcon: 'ri-global-line',
    tabLabel: messages.talenthubTab,
    title: messages.talenthubTitle,
    lede: messages.talenthubLede,
    features: [
      messages.talenthubFeature1,
      messages.talenthubFeature2,
      messages.talenthubFeature3,
    ],
    cta: messages.talenthubCta,
    rating: messages.talenthubRating,
    metaIcon: 'ri-timer3-fill',
    bannerImage: 'og/talenthub.png',
    bannerAlt: 'Glints TalentHub: managed remote teams in 150+ countries',
  },
};

const SOLUTION_KEYS = Object.keys(solutions) as SolutionKey[];

const tabId = (key: SolutionKey) => `landing-sg-tab-${key}`;
const panelId = (key: SolutionKey) => `landing-sg-tabpanel-${key}`;

export const OneGlints: React.FC = () => {
  const [activeTab, setActiveTab] = React.useState<SolutionKey>('tapjobs');
  const active = solutions[activeTab];

  // CTA destinations are environment-specific (employers base URL), so they're
  // resolved here at render rather than baked into the static `solutions` data.
  const talentHubLinks = getTalentHubLandingLinks(useEmployersURL());
  const ctaHrefByKey: Record<SolutionKey, string> = {
    tapjobs: talentHubLinks.hireSignup,
    talenthub: talentHubLinks.talentHub,
  };
  const metaGlyph = active.metaIcon ? (
    <Styled.MetaIcon aria-hidden="true">
      <Icon name={active.metaIcon} />
    </Styled.MetaIcon>
  ) : (
    <Styled.Stars aria-hidden="true">★★★★★</Styled.Stars>
  );

  const tabRefs = React.useRef<Record<SolutionKey, HTMLButtonElement | null>>({
    tapjobs: null,
    talenthub: null,
  });

  const focusTab = (key: SolutionKey) => {
    setActiveTab(key);
    tabRefs.current[key]?.focus();
  };

  const handleTabKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    key: SolutionKey
  ) => {
    const currentIndex = SOLUTION_KEYS.indexOf(key);
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      event.preventDefault();
      focusTab(SOLUTION_KEYS[(currentIndex + 1) % SOLUTION_KEYS.length]);
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      event.preventDefault();
      focusTab(
        SOLUTION_KEYS[
          (currentIndex - 1 + SOLUTION_KEYS.length) % SOLUTION_KEYS.length
        ]
      );
    } else if (event.key === 'Home') {
      event.preventDefault();
      focusTab(SOLUTION_KEYS[0]);
    } else if (event.key === 'End') {
      event.preventDefault();
      focusTab(SOLUTION_KEYS[SOLUTION_KEYS.length - 1]);
    }
  };

  return (
    <Styled.Section>
      <Styled.Inner>
        <Styled.Head>
          <FormattedMessage
            id="landing-sg.solutions.title"
            defaultMessage="One Glints, end-to-end <hl>talent solutions</hl>"
            values={{
              hl: (...chunks: React.ReactNode[]) => (
                <Styled.TitleHighlight>{chunks}</Styled.TitleHighlight>
              ),
            }}
            tagName={Styled.Title}
          />
          <FormattedMessage
            id="landing-global.solutions.description"
            defaultMessage="Two ways to hire, one partner. Run it yourself on our platform, or let us build and manage the team for you."
            tagName={Styled.HeadDescription}
          />
        </Styled.Head>

        <Styled.Card>
          <div>
            <Styled.Tabs role="tablist" aria-label="Glints solutions">
              {SOLUTION_KEYS.map((key) => {
                const isActive = key === activeTab;
                return (
                  <Styled.TabButton
                    key={key}
                    ref={(el: HTMLButtonElement | null) => {
                      tabRefs.current[key] = el;
                    }}
                    id={tabId(key)}
                    role="tab"
                    type="button"
                    $active={isActive}
                    aria-selected={isActive}
                    aria-controls={panelId(key)}
                    tabIndex={isActive ? 0 : -1}
                    onClick={() => setActiveTab(key)}
                    onKeyDown={(event) => handleTabKeyDown(event, key)}
                  >
                    <Styled.TabIcon aria-hidden="true">
                      <Icon name={solutions[key].tabIcon} />
                    </Styled.TabIcon>
                    <FormattedMessage
                      {...solutions[key].tabLabel}
                      tagName="span"
                    />
                  </Styled.TabButton>
                );
              })}
            </Styled.Tabs>

            <div
              role="tabpanel"
              id={panelId(activeTab)}
              aria-labelledby={tabId(activeTab)}
            >
              <FormattedMessage {...active.title} tagName={Styled.SolTitle} />
              <FormattedMessage {...active.lede} tagName={Styled.Lede} />

              <Styled.FeatureList>
                {active.features.map((feature) => (
                  <Styled.FeatureItem key={String(feature.id)}>
                    <FormattedMessage {...feature} tagName="span" />
                  </Styled.FeatureItem>
                ))}
              </Styled.FeatureList>

              <Styled.CtaRow>
                <Styled.Cta href={ctaHrefByKey[active.key]}>
                  <FormattedMessage {...active.cta} tagName="span" />
                  <ArrowRoundForwardIcon color={Greyscale.white} />
                </Styled.Cta>
              </Styled.CtaRow>

              <Styled.Meta>
                <Styled.Rating>
                  {metaGlyph}
                  <FormattedMessage
                    {...active.rating}
                    values={{
                      stars: '',
                      score: <strong>4.9 / 5</strong>,
                      count: '2700+',
                    }}
                    tagName={Styled.RatingText}
                  />
                </Styled.Rating>
              </Styled.Meta>
            </div>
          </div>

          <Styled.Visual
            aria-hidden={!active.bannerImage}
            $bare={Boolean(active.bannerImage)}
          >
            <If condition={Boolean(active.bannerImage)}>
              <Styled.VisualBanner
                name={active.bannerImage}
                assetType={AssetType.dstLandingSgPage}
                alt={active.bannerAlt ?? ''}
                sizes="560px"
                breakpoints={560}
                lazy={true}
              />
            </If>
            <If condition={!active.bannerImage}>
              <Styled.FloatBadge position="topLeft">
                <Styled.StatusDot />
                <FormattedMessage
                  id="landing-sg.solutions.visual.badge.matches"
                  defaultMessage="142 new matches today"
                  tagName="span"
                />
              </Styled.FloatBadge>
              <Styled.FloatBadge position="bottomRight">
                <Styled.VerifiedIcon>
                  <Icon name="ri-checkbox-circle-fill" />
                </Styled.VerifiedIcon>
                <FormattedMessage
                  id="landing-sg.solutions.visual.badge.verified"
                  defaultMessage="Verified employer"
                  tagName="span"
                />
              </Styled.FloatBadge>
              <Styled.JobMock>
                <Styled.JobMockHeader>
                  <Styled.JobMockBrand>G</Styled.JobMockBrand>
                  <Styled.MockTitleStack>
                    <Styled.MockBar width="58%" />
                    <Styled.MockBar width="38%" muted={true} />
                  </Styled.MockTitleStack>
                </Styled.JobMockHeader>
                <Styled.MockTagRow>
                  <Styled.MockTag>Remote</Styled.MockTag>
                  <Styled.MockTag>Full-time</Styled.MockTag>
                  <Styled.MockTag>SGD 6–9k</Styled.MockTag>
                </Styled.MockTagRow>
                <Styled.JobMockDivider />
                <Styled.JobMockMatchHeader>
                  <Icon name="ri-flashlight-fill" />
                  <span>AI-matched candidates</span>
                </Styled.JobMockMatchHeader>
                <Styled.JobMockCandidate>
                  <Styled.JobMockAvatar tone="blue" />
                  <Styled.JobMockCandidateBars>
                    <Styled.MockBar width="80%" />
                    <Styled.MockBar width="48%" muted={true} />
                  </Styled.JobMockCandidateBars>
                  <Styled.JobMockMatchChip strength="high">
                    92%
                  </Styled.JobMockMatchChip>
                </Styled.JobMockCandidate>
                <Styled.JobMockCandidate>
                  <Styled.JobMockAvatar tone="yellow" />
                  <Styled.JobMockCandidateBars>
                    <Styled.MockBar width="65%" />
                    <Styled.MockBar width="40%" muted={true} />
                  </Styled.JobMockCandidateBars>
                  <Styled.JobMockMatchChip strength="mid">
                    87%
                  </Styled.JobMockMatchChip>
                </Styled.JobMockCandidate>
                <Styled.JobMockCandidate>
                  <Styled.JobMockAvatar tone="dark" />
                  <Styled.JobMockCandidateBars>
                    <Styled.MockBar width="72%" />
                    <Styled.MockBar width="36%" muted={true} />
                  </Styled.JobMockCandidateBars>
                  <Styled.JobMockMatchChip strength="mid">
                    81%
                  </Styled.JobMockMatchChip>
                </Styled.JobMockCandidate>
              </Styled.JobMock>
            </If>
          </Styled.Visual>
        </Styled.Card>
      </Styled.Inner>
    </Styled.Section>
  );
};

export default OneGlints;
